"use client";

import { useContext } from "react";
import { useSession } from "../components/SupabaseProvider";
import SelectVideoPublicationForm from "../components/SelectVideoPublicationForm";
import {
  createOccupiedSlotSet,
  findNextRecommendedTimeSlot,
} from "../utils/publicationDistribution";
import { ModalContext } from "./useModal";
import { useReadPublications } from "./usePublications";
import { useResources } from "./useResources";

export type EmptyStateKind =
  | "loading"
  | "no_shorts"
  | "no_publications"
  | "has_publications";

export type EmptyStateStatus = { kind: EmptyStateKind };

/**
 * Three-state empty-state detection shared by /calendar and /analytics.
 *
 * Purely derived from live data so clearing the queue returns the user to
 * the appropriate earlier state:
 *   - has_publications — user currently has at least one publication.
 *   - no_shorts        — library is empty (no folders or top-level videos).
 *   - no_publications  — library has content but nothing is scheduled.
 *
 * Returns `loading` until both publications and resources have resolved.
 */
export const useEmptyStateStatus = (): EmptyStateStatus => {
  const session = useSession();
  const userId = session?.user?.id;
  const { data: publications, isLoading: isPublicationsLoading } =
    useReadPublications();
  const resources = useResources(userId ?? "");

  if (!userId) return { kind: "loading" };
  if (isPublicationsLoading || publications === undefined)
    return { kind: "loading" };
  if (resources.isLoading || resources.data === undefined)
    return { kind: "loading" };

  if (publications.length > 0) return { kind: "has_publications" };
  if (resources.data.length === 0) return { kind: "no_shorts" };
  return { kind: "no_publications" };
};

/**
 * Returns a callback that opens the publication scheduling dialog at the next
 * recommended time slot, mirroring the + button logic in `components/Calendar.tsx`.
 */
export const useOpenScheduleModal = () => {
  const modal = useContext(ModalContext);
  const { data: publications } = useReadPublications();

  return () => {
    const occupiedSlots = createOccupiedSlotSet(
      (publications ?? [])
        .map((p) => p.scheduledAt)
        .filter(
          (d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()),
        ),
    );
    const nextRecommendedSlot = findNextRecommendedTimeSlot(
      new Date(),
      occupiedSlots,
    );
    modal.open(
      <SelectVideoPublicationForm
        defaultDate={nextRecommendedSlot}
        from="calendar"
      />,
      {
        className: "h-full max-w-3xl",
        disabledPadding: true,
      },
    );
  };
};
